import React, { memo, useState } from 'react';
import {
  Select,
  MenuItem,
  Chip,
  Box,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
  OutlinedInput,
  SelectChangeEvent,
} from '@mui/material';
import './multipleSelect.css';

export interface SelectOption {
  id: string | number;
  label: string;
  value: string;
}

export interface CustomMultiSelectProps {
  options: SelectOption[];
  selectedValues: string[];
  onChange: (selectedValues: string[]) => void;
  placeholder?: string;
  label?: string;
  maxVisibleChips?: number;
  disabled?: boolean;
  className?: string;
  highlightCheckedColor?: string;
}

const CustomMultiSelect: React.FC<CustomMultiSelectProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder = 'Select items...',
  label = 'Select items',
  maxVisibleChips = 2,
  disabled = false,
  className = '',
  highlightCheckedColor = '',
}) => {
  const [open, setOpen] = useState(false);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onChange(typeof value === 'string' ? value.split(',') : value);
  };

  const handleDelete = (valueToDelete: string) => (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange(selectedValues.filter((value) => value !== valueToDelete));
  };

  const getSelectedOptions = () => {
    return options.filter((option) => selectedValues.includes(option.value));
  };

  const renderValue = (selected: string[]) => {
    const selectedOptions = getSelectedOptions();
    const visibleChips = selectedOptions.slice(0, maxVisibleChips);
    const remainingCount = selectedOptions.length - maxVisibleChips;

    return (
      <Box className="chipContainer">
        {visibleChips.map((option) => (
          <Chip
            key={option.value}
            label={option.label}
            className="selectedChip"
            onDelete={disabled ? undefined : handleDelete(option.value)}
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
            size="small"
          />
        ))}
        {remainingCount > 0 && (
          <Chip
            label={`+${remainingCount}`}
            className="countChip"
            size="small"
          />
        )}
      </Box>
    );
  };

  return (
    <FormControl className={`multiSelectForm ${className}`} fullWidth>
      <InputLabel className="selectLabel">{label}</InputLabel>
      <Select
        multiple
        value={selectedValues}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        renderValue={renderValue}
        className="multiSelectInput"
        disabled={disabled}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        MenuProps={{
          PaperProps: {
            className: 'selectMenuPaper',
            style: {
              maxHeight: 300,
            },
          },
          // Prevent menu from closing when clicking inside
          autoFocus: false,
          disableAutoFocusItem: true,
        }}
        displayEmpty
      >
        {placeholder && selectedValues.length === 0 && (
          <MenuItem disabled value="" className="placeholderItem">
            <em>{placeholder}</em>
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            className="selectOption"
            // Prevent menu from closing on item click
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <Checkbox
              checked={selectedValues.includes(option.value)}
              className="optionCheckbox"
              size="small"
              sx={{
                '&.Mui-checked': {
                  color: highlightCheckedColor || undefined,
                  '& svg': { color: highlightCheckedColor || undefined },
                },
              }}
            />
            <ListItemText primary={option.label} className="optionText" />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default memo(CustomMultiSelect);
